import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ShoppingBag, Plus, Trash, MagnifyingGlass, Receipt, Image as ImageIcon, CheckCircle, XCircle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useDataCache } from '@/contexts/DataCache';
import { useAuth } from '@/contexts/AuthContext';
import { HistoryDetailDialog } from '@/components/common/HistoryDetailDialog';
import { HistoryFilters } from '@/components/common/HistoryFilters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Persistencia local del borrador de compra (se pierde al cambiar de pantalla
// si no se guarda). Sólo lo guardado/enviado se borra explícitamente.
const DRAFT_KEY = 'pos_purchase_draft_v1';
const _loadDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
};

const _branchName = (branch_id, branches) => {
  if (!branch_id) return '-';
  const b = (branches || []).find(x => x.id === branch_id);
  if (b) return b.name;
  const s = String(branch_id).toLowerCase();
  if (s.includes('morelos')) return 'Morelos';
  if (s.includes('america') || s.includes('américa')) return 'América';
  return branch_id;
};

export const Purchases = () => {
  const { products, refreshProducts } = useDataCache();
  const { user } = useAuth();
  const isAdmin = user && user.role === 'admin';

  const [searchTerm, setSearchTerm] = useState('');
  const [cart, setCart] = useState(() => _loadDraft()?.cart || []);
  const [supplier, setSupplier] = useState(() => _loadDraft()?.supplier || '');
  const [notes, setNotes] = useState(() => _loadDraft()?.notes || '');
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [branches, setBranches] = useState([]);
  const [detail, setDetail] = useState({ open: false, record: null });

  // Success confirmation screen
  const [successData, setSuccessData] = useState(null);

  // Cancel modal
  const [cancelTarget, setCancelTarget] = useState(null); // full purchase record
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // History filters
  const [hSearch, setHSearch] = useState('');
  const [hFrom, setHFrom] = useState('');
  const [hTo, setHTo] = useState('');

  useEffect(() => {
    fetchHistory();
    (async () => {
      try {
        const { data } = await axios.get(`${API}/branches/list`, { withCredentials: true });
        setBranches(data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  // Persistir borrador en cada cambio
  useEffect(() => {
    try {
      if (cart.length === 0 && !supplier && !notes) {
        localStorage.removeItem(DRAFT_KEY);
      } else {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ cart, supplier, notes }));
      }
    } catch { /* ignore quota */ }
  }, [cart, supplier, notes]);

  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/purchases`, { withCredentials: true });
      setHistory(data);
    } catch (e) { /* ignore */ }
  };

  const hiddenParentIds = useMemo(() => {
    const parentIds = new Set(products.map(p => p.parent_product_id).filter(Boolean));
    return new Set(
      products
        .filter(p => (p.has_variants || parentIds.has(p.id)) && p.visible_in_pos === false)
        .map(p => p.id)
    );
  }, [products]);

  const filtered = products.filter((p) => {
    if (p.has_variants) return false;
    if (p.visible_in_pos === false) return false;
    if (p.parent_product_id && hiddenParentIds.has(p.parent_product_id)) return false;
    const term = searchTerm.toLowerCase();
    return (
      p.name.toLowerCase().includes(term) ||
      (p.sku || '').toLowerCase().includes(term)
    );
  }).slice(0, 100);

  const addToCart = (product) => {
    const existing = cart.find((c) => c.product_id === product.id);
    if (existing) {
      setCart(cart.map((c) => c.product_id === product.id ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.variant_name ? `${product.name} (${product.variant_name})` : product.name,
        sku: product.sku,
        image_url: product.image_url || null,
        current_stock: product.stock || 0,
        quantity: 1
      }]);
    }
  };

  const updateQty = (idx, qty) => {
    if (qty === '' || qty === null) {
      setCart(cart.map((c, i) => i === idx ? { ...c, quantity: '' } : c));
      return;
    }
    const n = parseInt(qty);
    if (!isFinite(n) || n < 0) return;
    setCart(cart.map((c, i) => i === idx ? { ...c, quantity: n } : c));
  };
  const removeFromCart = (idx) => setCart(cart.filter((_, i) => i !== idx));

  const totalQty = cart.reduce((s, c) => s + (parseInt(c.quantity) || 0), 0);

  const handleSubmit = async () => {
    if (cart.length === 0) { toast.error('Agrega al menos un producto'); return; }
    if (cart.some(c => !parseInt(c.quantity) || parseInt(c.quantity) < 1)) {
      toast.error('Todos los productos deben tener cantidad válida (≥1)');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: cart.map((c) => ({ product_id: c.product_id, product_name: c.product_name, quantity: parseInt(c.quantity) })),
        supplier_name: supplier || null,
        notes: notes || null
      };
      const { data } = await axios.post(`${API}/purchases`, payload, { withCredentials: true });
      toast.success(`Compra ${data.folio} registrada · +${data.total_qty} pzas al stock`);
      // Enriquecer con supplier/notes locales por si el back no los devuelve
      setSuccessData({
        ...data,
        supplier_name: data.supplier_name || supplier || null,
        notes: notes || null,
      });
      setCart([]); setSupplier(''); setNotes('');
      refreshProducts();
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar compra');
    } finally {
      setSubmitting(false);
    }
  };

  const openCancel = (purchase) => {
    setCancelTarget(purchase);
    setCancelReason('');
  };

  const submitCancel = async () => {
    if (!cancelTarget) return;
    const reason = cancelReason.trim();
    if (reason.length < 5) {
      toast.error('El motivo debe tener al menos 5 caracteres');
      return;
    }
    setCancelling(true);
    try {
      const { data } = await axios.post(
        `${API}/purchases/${cancelTarget.id}/cancel`,
        { reason },
        { withCredentials: true }
      );
      if (data && data.status === 'pending') {
        toast.success('Solicitud enviada a admin por Telegram · esperando aprobación', { duration: 6000 });
      } else {
        toast.success(`Compra ${cancelTarget.folio} cancelada · stock revertido`);
      }
      setCancelTarget(null);
      setCancelReason('');
      refreshProducts();
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar compra');
    } finally {
      setCancelling(false);
    }
  };

  // Filtros del historial
  const filteredHistory = useMemo(() => {
    return history.filter((h) => {
      if (hFrom && h.created_at < hFrom) return false;
      if (hTo && h.created_at > hTo + 'T23:59:59') return false;
      if (hSearch) {
        const t = hSearch.toLowerCase();
        const inFolio = (h.folio || '').toLowerCase().includes(t);
        const inSupplier = (h.supplier_name || '').toLowerCase().includes(t);
        const inNotes = (h.notes || '').toLowerCase().includes(t);
        const inItems = (h.items || []).some((it) => (it.product_name || '').toLowerCase().includes(t));
        const inUser = (h.created_by_name || '').toLowerCase().includes(t);
        if (!(inFolio || inSupplier || inNotes || inItems || inUser)) return false;
      }
      return true;
    });
  }, [history, hSearch, hFrom, hTo]);

  return (
    <MainLayout>
      <div className="p-8">
        <div className="flex items-center gap-3 mb-6">
          <ShoppingBag size={32} weight="bold" className="text-emerald-600" />
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Compras</h1>
            <p className="text-muted-foreground">Registra entradas de mercancía y aumenta el stock</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Catálogo compacto */}
          <div className="lg:col-span-5 bg-white border border-border rounded-sm p-3 flex flex-col">
            <div className="relative mb-2">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar…"
                className="pl-9 h-9 text-sm"
                data-testid="purchase-search"
              />
            </div>
            <div className="flex-1 overflow-auto space-y-1 max-h-[60vh]">
              {filtered.map((p) => (
                <button
                  key={p.id}
                  onClick={() => addToCart(p)}
                  className="w-full flex items-center gap-2 p-2 border border-border rounded hover:bg-emerald-50 hover:border-emerald-400 btn-transition text-left"
                  data-testid={`purchase-add-${p.id}`}
                >
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-10 h-10 object-cover rounded flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 bg-slate-100 rounded flex-shrink-0 flex items-center justify-center">
                      <ImageIcon size={18} className="text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.name}{p.variant_name ? ` (${p.variant_name})` : ''}</p>
                    <p className="text-[10px] text-muted-foreground">SKU: {p.sku} · Stock: <span className="font-semibold">{p.stock}</span></p>
                  </div>
                </button>
              ))}
              {filtered.length === 0 && (
                <p className="text-center text-muted-foreground py-6 text-sm">Sin productos</p>
              )}
            </div>
          </div>

          {/* Carrito de compra */}
          <div className="lg:col-span-7 bg-white border border-border rounded-sm p-4 flex flex-col">
            <h2 className="text-xl font-semibold mb-3">Items a ingresar</h2>
            <div className="flex-1 overflow-auto space-y-2 mb-4 max-h-[55vh]">
              {cart.map((c, i) => (
                <div key={i} className="border border-border rounded p-2 flex items-start gap-3" data-testid={`purchase-cart-item-${i}`}>
                  {c.image_url ? (
                    <img src={c.image_url} alt={c.product_name} className="w-14 h-14 object-cover rounded flex-shrink-0" />
                  ) : (
                    <div className="w-14 h-14 bg-slate-100 rounded flex-shrink-0 flex items-center justify-center">
                      <ImageIcon size={22} className="text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{c.product_name}</p>
                    <p className="text-xs text-muted-foreground">SKU: {c.sku} · Stock: {c.current_stock} → <span className="text-emerald-600 font-semibold">{c.current_stock + c.quantity}</span></p>
                    <div className="flex items-center gap-2 mt-1">
                      <Label className="text-xs">Cant:</Label>
                      <Input
                        type="number" min="1" value={c.quantity}
                        onChange={(e) => updateQty(i, e.target.value)}
                        className="w-24 h-8" data-testid={`purchase-qty-${i}`}
                      />
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeFromCart(i)} data-testid={`purchase-remove-${i}`}><Trash size={14} /></Button>
                </div>
              ))}
              {cart.length === 0 && <p className="text-muted-foreground text-center py-8">Sin items</p>}
            </div>

            <div className="border-t pt-3 space-y-2">
              <div>
                <Label htmlFor="supplier">Proveedor (opcional)</Label>
                <Input id="supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} data-testid="purchase-supplier" />
              </div>
              <div>
                <Label htmlFor="notes">Notas (opcional)</Label>
                <Textarea id="notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="purchase-notes" />
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-sm text-muted-foreground">Total piezas:</span>
                <span className="text-2xl font-bold text-emerald-600">{totalQty}</span>
              </div>
              <Button onClick={handleSubmit} disabled={submitting || cart.length === 0} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="purchase-submit">
                <Plus size={16} className="mr-1" />
                {submitting ? 'Registrando…' : 'Registrar compra'}
              </Button>
            </div>
          </div>
        </div>

        {/* Historial */}
        <div className="mt-8 bg-white border border-border rounded-sm">
          <h2 className="text-lg font-semibold p-4 border-b flex items-center gap-2">
            <Receipt size={20} />
            Historial de compras <span className="text-xs font-normal text-muted-foreground">({filteredHistory.length} de {history.length})</span>
          </h2>
          <div className="p-3">
            <HistoryFilters
              searchTerm={hSearch} onSearch={setHSearch}
              dateFrom={hFrom} onDateFrom={setHFrom}
              dateTo={hTo} onDateTo={setHTo}
            />
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Folio</th>
                <th className="px-3 py-2 text-left">Fecha</th>
                <th className="px-3 py-2 text-left">Proveedor</th>
                <th className="px-3 py-2 text-left">Por</th>
                <th className="px-3 py-2 text-right">Piezas</th>
                <th className="px-3 py-2 text-left">Items</th>
                <th className="px-3 py-2 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredHistory.map((h) => {
                const isCancelled = h.status === 'cancelled';
                return (
                  <tr
                    key={h.id}
                    onDoubleClick={() => setDetail({ open: true, record: h })}
                    className={`hover:bg-slate-50 cursor-pointer ${isCancelled ? 'bg-red-50/40 text-slate-500' : ''}`}
                    title="Doble clic para ver detalle"
                    data-testid={`purchase-history-${h.id}`}
                  >
                    <td className={`px-3 py-2 font-mono text-xs ${isCancelled ? 'line-through' : ''}`}>
                      {h.folio}
                      {isCancelled && (
                        <span className="ml-2 inline-block bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-sm font-bold align-middle no-underline">
                          CANCELADA
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">{new Date(h.created_at).toLocaleString('es-MX')}</td>
                    <td className="px-3 py-2">{h.supplier_name || '-'}</td>
                    <td className="px-3 py-2 text-xs">{h.created_by_name || '-'}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${isCancelled ? 'line-through' : ''}`}>{h.total_qty}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {(h.items || []).slice(0, 3).map((it) => `${it.quantity}× ${it.product_name}`).join(', ')}
                      {(h.items || []).length > 3 && ` … (+${h.items.length - 3} más)`}
                      {isCancelled && h.cancellation_reason && (
                        <div className="mt-1 text-[10px] text-red-700 no-underline">
                          <b>Motivo:</b> {h.cancellation_reason}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {!isCancelled ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); openCancel(h); }}
                          className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                          data-testid={`purchase-cancel-btn-${h.id}`}
                        >
                          <XCircle size={12} className="mr-1" /> Cancelar
                        </Button>
                      ) : (
                        <span className="text-[10px] text-red-600 font-semibold">CANCELADA</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filteredHistory.length === 0 && (
                <tr><td colSpan="7" className="px-3 py-8 text-center text-muted-foreground">Sin resultados</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {/* Pantalla de confirmación tras registrar compra */}
      <Dialog
        open={!!successData}
        onOpenChange={(o) => { if (!o) setSuccessData(null); }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="purchase-success-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle size={26} weight="fill" className="text-emerald-600" />
              Compra registrada correctamente
            </DialogTitle>
          </DialogHeader>
          {successData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm bg-emerald-50 border border-emerald-200 rounded p-3">
                <div>
                  <p className="text-[10px] uppercase text-emerald-800 font-semibold">Folio</p>
                  <p className="font-mono font-bold" data-testid="purchase-success-folio">{successData.folio}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-emerald-800 font-semibold">Fecha</p>
                  <p className="font-semibold">
                    {new Date(successData.created_at).toLocaleString('es-MX')}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-emerald-800 font-semibold">Sucursal</p>
                  <p className="font-semibold" data-testid="purchase-success-branch">
                    {successData.branch_name || _branchName(successData.branch_id, branches)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-emerald-800 font-semibold">Total piezas</p>
                  <p className="font-bold text-emerald-700 text-lg">+{successData.total_qty}</p>
                </div>
                {successData.supplier_name && (
                  <div className="col-span-2">
                    <p className="text-[10px] uppercase text-emerald-800 font-semibold">Proveedor</p>
                    <p className="font-semibold">{successData.supplier_name}</p>
                  </div>
                )}
                {successData.notes && (
                  <div className="col-span-2">
                    <p className="text-[10px] uppercase text-emerald-800 font-semibold">Notas</p>
                    <p className="text-xs">{successData.notes}</p>
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm font-semibold mb-2">Productos ingresados ({(successData.items || []).length})</p>
                <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
                  {(successData.items || []).map((it, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 border border-border rounded p-2 bg-white"
                      data-testid={`purchase-success-item-${i}`}
                    >
                      {it.image_url ? (
                        <img
                          src={it.image_url}
                          alt={it.product_name}
                          className="w-16 h-16 object-cover rounded flex-shrink-0"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-slate-100 rounded flex-shrink-0 flex items-center justify-center">
                          <ImageIcon size={26} className="text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{it.product_name}</p>
                        {it.sku && <p className="text-[10px] text-muted-foreground">SKU: {it.sku}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-emerald-600 leading-none">×{it.quantity}</p>
                        <p className="text-[10px] uppercase text-muted-foreground">piezas</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => setSuccessData(null)}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="purchase-success-close"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal cancelar compra */}
      <Dialog
        open={!!cancelTarget}
        onOpenChange={(o) => { if (!o && !cancelling) { setCancelTarget(null); setCancelReason(''); } }}
      >
        <DialogContent className="max-w-md" data-testid="purchase-cancel-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Warning size={22} weight="fill" className="text-red-600" />
              Cancelar compra {cancelTarget?.folio}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!isAdmin && (
              <div className="text-xs bg-amber-50 border border-amber-300 text-amber-900 rounded p-2">
                <b>Atención:</b> Se enviará una solicitud al admin por Telegram para aprobar la cancelación. El stock <b>no se restará</b> hasta que sea aprobada.
              </div>
            )}
            {isAdmin && (
              <div className="text-xs text-muted-foreground">
                Se restarán <b>{cancelTarget?.total_qty || 0} pzas</b> del stock de <b>{_branchName(cancelTarget?.branch_id, branches)}</b>.
              </div>
            )}
            <div>
              <Label htmlFor="cancel-reason">Motivo de cancelación *</Label>
              <Textarea
                id="cancel-reason"
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Ej: Producto equivocado, factura duplicada, error de captura…"
                data-testid="purchase-cancel-reason"
                autoFocus
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Mínimo 5 caracteres · {cancelReason.trim().length}/5
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setCancelTarget(null); setCancelReason(''); }}
              disabled={cancelling}
              data-testid="purchase-cancel-abort"
            >
              No cancelar
            </Button>
            <Button
              onClick={submitCancel}
              disabled={cancelling || cancelReason.trim().length < 5}
              className="bg-red-600 hover:bg-red-700"
              data-testid="purchase-cancel-confirm"
            >
              {cancelling ? (isAdmin ? 'Cancelando…' : 'Enviando…') : (isAdmin ? 'Confirmar cancelación' : 'Enviar solicitud a admin')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <HistoryDetailDialog
        open={detail.open}
        onOpenChange={(o) => setDetail({ open: o, record: o ? detail.record : null })}
        kind="purchase"
        record={detail.record}
      />
    </MainLayout>
  );
};
